import React, { useEffect, useState } from "react";
import axios from "axios";

/**
 * AssetImage Component
 * Specifically handles fetching binary image blobs from the Asset API
 * and rendering them using an <img> tag.
 *
 * Props:
 * - fileID: The ID of the asset file to fetch.
 * - alt: Alternative text for the image.
 * - className: Custom CSS classes for styling the image/container.
 * - fallback: Optional fallback image URL to show on error.
 */
const TSImage = ({ fileID, fallback = null, ...props }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl = null;

    const fetchImage = async () => {
      if (!fileID) return;

      try {
        setLoading(true);
        setError(false);

        const response = await axios.get(`/api/v1/file/asset/${fileID}`, {
          responseType: "blob",
        });

        const contentType = response.headers["content-type"];

        if (contentType && contentType.startsWith("image/")) {
          objectUrl = URL.createObjectURL(response.data);
          setImgSrc(objectUrl);
        } else {
          console.error("Response was not an image. Content-Type:", contentType);
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching asset image:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileID]);

  // Rendering Loading State
  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 animate-pulse rounded ${props.className || ""}`}>
        <span className="text-xs text-gray-500 font-medium">Loading...</span>
      </div>
    );
  }

  // Rendering Error/Fallback State
  if (error || !imgSrc) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 rounded ${props.className || ""}`}>
        {fallback ? (
          <img src={fallback} alt={props.alt || "Asset Image"} className="w-full h-full object-cover" />
        ) : (
          <div className="text-center p-2">
            <span className="text-2xl">🖼️</span>
            <p className="text-xs text-gray-400 mt-1">No Image</p>
          </div>
        )}
      </div>
    );
  }

  // Rendering the actual image
  return (
    <img
      {...props}
      src={imgSrc}
      onError={() => setError(true)}
    />
  );
};

export default TSImage;
